// useFullStory.ts
import { useEffect } from "react";
import { AppState, Platform, AppStateStatus } from "react-native";
import { useAtom } from "jotai";
import { userAtom, userStateAtom } from "../atoms";
import {
  getLastStreamWatchedFromStorage,
  removeLastStreamWatchedFromStorage,
} from "../utils/fullStory";
import { logEvent } from "../client/analytics/trackPageView";
import FullStory from "../services/FullStory";

/**
 * useFullStory hook for interacting with FullStory.
 *
 * @returns {Function} - An object containing the function to send exit stream tag event to FullStory.
 * @description This hook is used to integrate FullStory into the application.
 * It identifies the user when the user changes and sends exit stream tag events to FullStory
 * when the app goes into the background or when the user navigates away from the web app.
 */
export const useFullStory = () => {
  // TODO - we don't need whole user objecthere - just userDetail
  // can likely remove userState too
  const [user] = useAtom(userAtom);
  const [userState] = useAtom(userStateAtom);

  // Identify the user for FullStory when user changes.
  useEffect(() => {
    if (user?.detail?.csn) {
      const {
        detail: { csn, email, name_family, name_given },
      } = user;

      FullStory.identifyUser({
        uid: csn,
        data: {
          displayName: `${name_given} ${name_family}`,
          email,
          pricing_plan: userState,
        },
      });
    }
  }, [user]);

  //
  // Functions to fire Exit Events to fullstory
  //

  // Exit Stream Event
  const sendExitStreamTagEventToFullStory = async () => {
    try {
      const lastStreamName = await getLastStreamWatchedFromStorage();
      if (lastStreamName) {
        logEvent({
          key: "track-video-stream",
          data: { stream_tag: `${lastStreamName}-exit` },
        });
        await removeLastStreamWatchedFromStorage();
      }
    } catch (e) {
      console.error("Error sending exit stream tag to FullStory", e);
    }
  };

  // Fire Exit Stream Event (web)
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleBeforeUnload = () => {
      sendExitStreamTagEventToFullStory();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Fire Exit Stream Event (native apps)
  useEffect(() => {
    if (Platform.OS === "web") return;

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === "background") {
        sendExitStreamTagEventToFullStory();
      }
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, []);

  return { sendExitStreamTagEventToFullStory };
};
